<template>
  <div v-if="loggedIn" class="ab" :class="{ embedded }">
    <!-- Titled card — mirrors the horoscope "Tune your reading" card so it's
         clearly a form you're working on. -->
    <button v-if="!embedded" class="ab-close" aria-label="Close" title="Close" @click="done">&times;</button>
    <h3 v-if="!embedded" class="ab-title-main">{{ title }}</h3>
    <div class="ab-bar">
      <div class="ab-tabs">
        <button class="ab-tab" :class="{ on: mode === 'simple' }" @click="setMode('simple')">Simple</button>
        <button class="ab-tab" :class="{ on: mode === 'advanced' }" @click="setMode('advanced')">Advanced</button>
      </div>
    </div>

    <!-- SIMPLE: three high-impact dropdowns — these anchor the image. -->
    <div v-if="mode === 'simple'" class="ab-fields">
      <p v-if="isSign" class="ab-sub">Shape the look of your <strong>{{ label || 'sign' }}</strong> image - the sign always stays the star. Switch to <strong>Advanced</strong> to refine it with Mystic AIda.</p>
      <p v-else-if="isAvatar" class="ab-sub">Choose how you appear. Switch to <strong>Advanced</strong> to shape your avatar with Mystic AIda (aura, era, anything else).</p>
      <p v-else class="ab-sub">Choose your look and what's sacred to you. Switch to <strong>Advanced</strong> to shape your {{ noun }} with Mystic AIda.</p>
      <div class="ab-row">
        <div class="ab-label">Art style</div>
        <AltarMultiSelect :model="styleArr" :options="SUGGEST.style" placeholder="Choose a style…"
                          @update="setStyle($event)" />
      </div>

      <!-- Sign: Mood/energy only on Simple (Symbol treatment & Setting live in Advanced). -->
      <template v-if="isSign">
        <div class="ab-row">
          <div class="ab-label">Mood / energy</div>
          <AltarMultiSelect :model="moodArr" :options="SUGGEST.signMood" placeholder="Choose a mood…"
                            @update="setMood($event)" />
        </div>
      </template>

      <!-- Avatar: Species + Presentation. Other surfaces: Paths + Atmosphere. -->
      <template v-else-if="isAvatar">
        <div class="ab-row">
          <div class="ab-label">Species / being <span class="ab-hint"> - pick one or mix for a hybrid</span></div>
          <AltarMultiSelect :model="facets.species" :options="SUGGEST.species" placeholder="Choose one or more…"
                            @update="facets.species = $event" />
        </div>
        <div class="ab-row">
          <div class="ab-label">Presentation</div>
          <AltarMultiSelect :model="presentationArr" :options="SUGGEST.presentation" placeholder="Choose a presentation…"
                            @update="setPresentation($event)" />
        </div>
      </template>
      <template v-else>
        <div class="ab-row">
          <div class="ab-label">Paths &amp; traditions</div>
          <AltarMultiSelect :model="facets.paths" :options="SUGGEST.paths" placeholder="Choose your paths…"
                            @update="facets.paths = $event" />
        </div>
        <div class="ab-row">
          <div class="ab-label">Atmosphere</div>
          <AltarMultiSelect :model="moodArr" :options="SUGGEST.mood" placeholder="Choose a mood…"
                            @update="setMood($event)" />
        </div>
      </template>
    </div>

    <!-- ADVANCED: the actual prompt description, shaped by the digital assistant. -->
    <div v-else class="ab-fields">
      <p class="ab-sub">This is the description your {{ noun }} is painted from. Edit it directly, or use the
        <strong>Mystic AIda</strong> assistant (opening now) to shape it - it keeps your Simple selections as the anchor.</p>
      <div class="ab-row">
        <div class="ab-label-row">
          <span class="ab-label">{{ nounCap }} description</span>
          <button class="ab-rebuild" :disabled="rebuilding" @click="rebuildFromSimple">
            <i class="fas fa-rotate"></i> {{ rebuilding ? 'Building…' : 'Rebuild from my selections' }}
          </button>
        </div>
        <textarea class="ab-input ab-prompt" v-model="prompt" rows="7"
                  :placeholder="`Describe your ${noun} - or tap Rebuild to draft it from your Simple selections.`"></textarea>
      </div>
    </div>

    <!-- Generate -->
    <div class="ab-actions">
      <button class="ab-btn" :disabled="generating || !canPaint" @click="generate">
        <i class="fas fa-wand-magic-sparkles"></i>
        {{ generating ? 'Painting…' : `${selectedUrl ? 'Re-paint' : 'Paint'} my ${noun}` }}
      </button>
      <span class="ab-cost">{{ costLabel }}</span>
    </div>

    <!-- Gallery / archive — tap a thumbnail to view it full + set it live. -->
    <div v-if="images.length" class="ab-gallery">
      <span class="ab-gallery-label">Your {{ noun }}s · tap to view &amp; set live</span>
      <div class="ab-grid">
        <button v-for="img in images" :key="img.id" class="ab-thumb" :class="{ on: img.is_selected }" @click="lightboxImg = img">
          <img :src="img.url" alt="" />
          <span v-if="img.is_selected" class="ab-thumb-tag">Live</span>
        </button>
      </div>
    </div>

    <!-- Full-image lightbox: review the image, then set it live from here. -->
    <div v-if="lightboxImg" class="ab-lightbox" @click.self="lightboxImg = null">
      <div class="ab-lightbox-card">
        <img :src="lightboxImg.url" class="ab-lightbox-img" alt="" />
        <div class="ab-lightbox-actions">
          <button class="ab-lightbox-ghost" @click="lightboxImg = null">Close</button>
          <button v-if="lightboxImg.is_selected" class="ab-lightbox-live" disabled>✓ Live</button>
          <button v-else class="ab-lightbox-set" :disabled="selectingLive" @click="setLiveFromLightbox">
            {{ selectingLive ? 'Setting…' : 'Set as Live' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/**
 * AltarBuilder — the altar customization panel (flips in from the Altar Home).
 * Simple = dropdowns that ANCHOR the altar (no assistant). Advanced = the actual
 * prompt DESCRIPTION the image is painted from, shaped by the Mystic AIda digital
 * assistant (the ai-zone chat) — which only opens on the Advanced tab.
 */
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue'
import { store } from '@/store'
import { formatAmount } from '@/utils/carats'
import { paintCostLabel, repaintLaterWords } from '@/utils/paint-cost'
import { useNotification } from '@kyvg/vue3-notification'
import {
  getAltarSpace, saveAltarSpace, generateAltarImage, selectAltarImage, compileAltarPrompt,
} from '@/api-service'
import AltarMultiSelect from '@/components/ui/AltarMultiSelect.vue'
import { isSignSubject } from '@/utils/chart-signs'

const emit = defineEmits(['done'])

// Done always dismisses the scoped Digital Assistant too, no matter which
// surface hosts this builder (avatar / altar / keeper).
function done() {
  window.dispatchEvent(new CustomEvent('altar-open-assistant', { detail: { open: false } }))
  emit('done')
}
const props = defineProps({
  // 'altar' (the Altar Home image), 'keeper' (the Keeper's avatar), 'avatar'
  // (the seeker's Mirror avatar), 'sun_sign' / 'moon_sign' / 'rising_sign' (the
  // seeker's custom sign image), or the 'reader_'-prefixed versions of those
  // three (the Mystic's brand-facing sign image, painted from the Studio).
  subject: { type: String, default: 'altar' },
  // For sign subjects: the friendly label shown in the title (e.g. "Leo Sun").
  label: { type: String, default: '' },
  // Inside a StudioEditModal the modal owns the title and the close; the
  // builder's own would double them.
  embedded: { type: Boolean, default: false },
})
const isKeeper = computed(() => props.subject === 'keeper')
const isAvatar = computed(() => props.subject === 'avatar')
const isSign = computed(() => isSignSubject(props.subject))
const noun = computed(() => isSign.value ? 'sign image'
  : (isAvatar.value ? 'avatar' : (isKeeper.value ? 'Keeper' : 'altar')))
const nounCap = computed(() => isSign.value ? 'Sign'
  : (isAvatar.value ? 'Avatar' : (isKeeper.value ? 'Keeper' : 'Altar')))
const title = computed(() => (isSign.value && props.label)
  ? `Paint your ${props.label}` : `Paint your ${noun.value}`)
const { notify } = useNotification()
const loggedIn = computed(() => !!store.querent_id)

// Kept lean (3 high-impact controls) so each visibly shapes the image. Symbols
// and objects now live in the Advanced description / assistant, not as dropdowns.
const SUGGEST = {
  style: ['Illustrated', 'Animated', 'Watercolor', 'Storybook', 'Stained glass', 'Ethereal', 'Realistic'],
  paths: ['Tarot', 'Astrology', 'Buddhism', 'Christianity', 'Kabbalah', 'Hermeticism', 'Wicca',
          'Heathenry', 'Hoodoo', 'Hinduism', 'Sufism', 'Stoicism', 'Animism', 'Ancestral veneration',
          'Shamanism', 'Taoism', 'Paganism', 'Secular / spiritual'],
  mood: ['candlelit and intimate', 'cosmic and vast', 'forest grove', 'serene and minimal',
         'oceanic and deep', 'ancient temple', 'cathedral light', 'starlit night'],
  // Avatar facets.
  species: ['Human', 'Elf', 'Faerie', 'Angelic', 'Demon', 'Spirit', 'Celestial', 'Elemental',
            'Beast / animal', 'Dragon', 'Merfolk', 'Vampire', 'Android / construct', 'Shapeshifter'],
  presentation: ['Feminine', 'Masculine', 'Androgynous'],
  // Sign facets — the sign itself (glyph + figure) always stays the dominant subject.
  treatment: ['Archetypal figure', 'Glyph-forward', 'Constellation'],
  signMood: ['fierce and bold', 'serene and calm', 'regal and proud', 'cosmic and vast',
             'dreamy and ethereal', 'dark and mysterious', 'warm and radiant'],
  setting: ['a cosmic starlit void', 'an elemental nature scene', 'an abstract dreamscape',
            'a temple of light', 'swirling galaxies', 'a misty mountaintop'],
}

const mode = ref('simple')
const facets = reactive({ paths: [], symbols: [], guides: [], sacred_objects: [], mood: '', style: '', species: [], presentation: '', belief_statement: '' })
const prompt = ref('')
const images = ref([])
const selectedUrl = ref(null)
// Null until the server says. These were hardcoded 2 and 25, which had both
// drifted — the allowance moved to 5 and the price to 50 carats — so the panel
// flashed two wrong numbers before the first fetch landed.
const freeRemaining = ref(null)
const cost = ref(null)
const balance = ref(0)
const generating = ref(false)
const rebuilding = ref(false)
const lightboxImg = ref(null)
const selectingLive = ref(false)
let saveTimer = null

// Single-pick facets rendered through the multi-select.
const moodArr = computed(() => (facets.mood ? [facets.mood] : []))
function setMood(arr) { facets.mood = arr.length ? arr[arr.length - 1] : '' }
const styleArr = computed(() => (facets.style ? [facets.style] : []))
function setStyle(arr) { facets.style = arr.length ? arr[arr.length - 1] : '' }
const presentationArr = computed(() => (facets.presentation ? [facets.presentation] : []))
function setPresentation(arr) { facets.presentation = arr.length ? arr[arr.length - 1] : '' }

const hasBeliefs = computed(() => isSign.value
  ? (facets.style || facets.mood)
  : (isAvatar.value
    ? (facets.style || facets.species.length || facets.presentation)
    : (facets.paths.length || facets.mood || facets.style ||
       facets.symbols.length || facets.sacred_objects.length)))
const canPaint = computed(() => hasBeliefs.value || prompt.value.trim())

// Before the Community opens a portrait repaint is free: no price at all (quest journey API v1 §6).
const costLabel = computed(() => paintCostLabel({ freeRemaining: freeRemaining.value, cost: cost.value, balance: balance.value,
  pricesVisible: store.prices_visible !== false }))

function apply(data) {
  if (data.facets) Object.assign(facets, {
    paths: data.facets.paths || [], symbols: data.facets.symbols || [],
    guides: data.facets.guides || [], sacred_objects: data.facets.sacred_objects || [],
    mood: data.facets.mood || '', style: data.facets.style || '',
    species: data.facets.species || [], presentation: data.facets.presentation || '',
    belief_statement: data.facets.belief_statement || '',
  })
  if (typeof data.prompt === 'string') prompt.value = data.prompt
  images.value = data.images || []
  selectedUrl.value = data.selected_image_url || null
  if (typeof data.free_remaining === 'number') freeRemaining.value = data.free_remaining
  if (typeof data.cost === 'number') cost.value = data.cost
  if (typeof data.balance === 'number') {
    balance.value = data.balance
    store.querent_carat_balance = data.balance   // keep the top-bar counter in sync
  }
}

async function load() {
  if (!store.session_id) return
  try { apply(await getAltarSpace(store.session_id, props.subject)) } catch (e) { /* */ }
}

// Debounced save of facets + the Advanced prompt (so edits persist).
function queueSave() {
  if (saveTimer) clearTimeout(saveTimer)
  saveTimer = setTimeout(async () => {
    try { await saveAltarSpace(store.session_id, { ...facets, compiled_prompt: prompt.value }, props.subject) } catch (e) { /* */ }
  }, 600)
}

// Studio-style suggestion chips derived from the Simple selections — shown in
// the assistant so the seeker can shape the description in one tap.
function buildSuggestions() {
  const s = []
  if (facets.style) s.push(`Make it more ${facets.style.toLowerCase()}`)
  if (isSign.value) {
    s.push('Keep the sign the dominant focus')
    if (facets.mood) s.push(`Lean into a ${facets.mood} energy`)
    s.push('Set a vivid scene around the sign')
    s.push('Add rich elemental detail around the sign')
    s.push('Make the colors more luminous')
    return s.slice(0, 5)
  }
  if (isAvatar.value) {
    if (facets.species.length) s.push(`Blend the ${facets.species.join(' + ')} traits`)
    s.push('Give them a striking, expressive face')
    s.push('Add a moonlit, cosmic aura')
  } else {
    if (facets.paths.length) s.push(`Show my ${facets.paths.slice(0, 2).join(' and ')} clearly`)
    if (facets.mood) s.push(`Lean into a ${facets.mood} atmosphere`)
    s.push(isKeeper.value ? 'Make the face warmer and wiser' : 'Add candles and incense')
  }
  s.push(`Make my ${noun.value} line up better with my selections`)
  return s.slice(0, 5)
}

// Switching tabs drives the digital assistant: only Advanced opens it.
async function setMode(m) {
  mode.value = m
  if (m === 'advanced') {
    window.dispatchEvent(new CustomEvent('altar-open-assistant', {
      detail: { open: true, scope: props.subject, suggestions: buildSuggestions() },
    }))
    if (!prompt.value.trim()) await rebuildFromSimple()
  } else {
    window.dispatchEvent(new CustomEvent('altar-open-assistant', { detail: { open: false } }))
  }
}

async function rebuildFromSimple() {
  if (rebuilding.value) return
  rebuilding.value = true
  try {
    const data = await compileAltarPrompt(store.session_id, { ...facets }, props.subject)
    apply(data)
  } catch (e) { /* */ } finally { rebuilding.value = false }
}

async function generate() {
  if (generating.value || !canPaint.value) return
  // Flip the button to "Painting…" IMMEDIATELY (before any await) so it reflects
  // the click at once and the guard above blocks double-clicks.
  generating.value = true
  try {
    // Simple "Paint" rebuilds the description from the CURRENT selections (so
    // changing dropdowns actually changes the image, and each rebuild varies).
    // Advanced "Paint" uses the description the seeker/assistant shaped.
    if (mode.value === 'simple') {
      await rebuildFromSimple()
    } else {
      try { await saveAltarSpace(store.session_id, { ...facets, compiled_prompt: prompt.value }, props.subject) } catch (e) { /* */ }
    }
    const data = await generateAltarImage(store.session_id, { ...facets, compiled_prompt: prompt.value }, props.subject)
    if (data && data.ok === false && data.reason === 'insufficient_carats') {
      notify({ type: 'warn', text: `Not enough carats - ${formatAmount(cost.value)} needed for another image.` })
      apply(data); return
    }
    apply(data)
    notify({ type: 'success', text: `Your ${noun.value} has been painted.` })
    window.dispatchEvent(new CustomEvent('altar-image-updated', { detail: { url: selectedUrl.value, subject: props.subject } }))
  } catch (e) {
    const later = repaintLaterWords(e)
    notify(later ? { type: 'warn', text: later } : { type: 'error', text: 'The image could not be painted. Try again.' })
  } finally { generating.value = false }
}

async function select(img) {
  if (img.is_selected) return
  try {
    apply(await selectAltarImage(store.session_id, img.id, props.subject))
    window.dispatchEvent(new CustomEvent('altar-image-updated', { detail: { url: selectedUrl.value, subject: props.subject } }))
  } catch (e) { /* */ }
}

// Set the lightboxed image live, then close the lightbox.
async function setLiveFromLightbox() {
  if (!lightboxImg.value || selectingLive.value) return
  selectingLive.value = true
  try {
    await select(lightboxImg.value)
    lightboxImg.value = null
  } catch (e) { /* */ } finally { selectingLive.value = false }
}

// The Advanced digital assistant (ai-zone) shapes the description: when it
// returns a revision, drop it into the textarea.
function onPromptSuggested(e) {
  const d = e.detail || {}
  if (d.prompt && (d.subject || 'altar') === props.subject) prompt.value = d.prompt
}

watch(facets, queueSave, { deep: true })
watch(prompt, queueSave)
// Switching subject (altar ↔ keeper) on the same instance — reset to Simple and
// reload that subject's facets/images.
watch(() => props.subject, () => { mode.value = 'simple'; prompt.value = ''; load() })
onMounted(() => {
  // Load when the session ARRIVES. On a cold reload this mounts before
  // start-session comes back, so store.session_id is still '' and the load
  // bails — and a bare call here never tried again, leaving an empty panel
  // that only filled in if you navigated away and came back. immediate:true
  // covers the already-warm case; watch fires only on a CHANGE, so nothing
  // refetches.
  watch(() => store.session_id, (id) => { if (id) load() }, { immediate: true })

  window.addEventListener('altar-prompt-suggested', onPromptSuggested)
})
onUnmounted(() => window.removeEventListener('altar-prompt-suggested', onPromptSuggested))
defineExpose({ load })
</script>

<style scoped>
/* Card chrome mirrors the horoscope "Tune your reading" card. */
.ab { position: relative; color: #e9dcff; padding: 18px 20px 22px;
  background: linear-gradient(160deg, rgba(93,59,140,0.22), rgba(28,16,48,0.35));
  border: 1px solid rgba(240,217,163,0.18); border-radius: 14px; }
.ab.embedded { padding: 4px 0 8px; background: transparent; border: none; border-radius: 0; }
.ab-title-main { font-family: 'Cinzel', serif; font-size: 19px; color: #f5e8c0; text-align: center;
  margin: 0 0 12px; padding: 0 34px; }
/* Close affordance matches every other modal in the app (.bd-close,
   .cv-modal-close): an × on the RIGHT. It used to be a "← Done" on the left,
   which read as a back-nav out of a flow rather than dismissing a panel. */
.ab-close { position: absolute; top: 12px; right: 14px; background: none; border: none;
  color: #9b86c9; font-size: 24px; line-height: 1; cursor: pointer; font-family: inherit;
  padding: 2px 6px; border-radius: 8px; }
.ab-close:hover { color: #f0d9a3; }
.ab-close:focus-visible { outline: 2px solid #FFDFA3; outline-offset: 2px; }
.ab-bar { display: flex; justify-content: center; }
.ab-tabs { display: flex; gap: 4px; background: rgba(20,8,40,0.5); border-radius: 999px; padding: 3px; }
.ab-tab { background: none; border: none; color: #9b86c9; font-size: 12.5px; font-weight: 600; padding: 5px 14px;
  border-radius: 999px; cursor: pointer; font-family: inherit; }
.ab-tab.on { background: rgba(102,51,153,0.5); color: #f0d9a3; }
.ab-sub { font-size: 12.5px; color: #b9a9d8; line-height: 1.5; margin: 12px 0 4px; }

.ab-fields { margin-top: 6px; }
.ab-row { margin-top: 16px; }
.ab-label { font-size: 11px; color: #9b86c9; }
.ab-hint { color: #7c6ba0; font-style: italic; }
.ab-label-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.ab-rebuild { background: rgba(102,51,153,0.18); border: 1px solid rgba(102,51,153,0.4); color: rgba(255,223,163,0.85);
  border-radius: 999px; padding: 5px 11px; font-size: 11.5px; cursor: pointer; font-family: inherit; }
.ab-rebuild:hover { background: rgba(102,51,153,0.32); color: #FFDFA3; }
.ab-rebuild:disabled { opacity: 0.5; cursor: default; }
.ab-input { width: 100%; box-sizing: border-box; margin-top: 6px; padding: 10px 12px; border-radius: 8px;
  border: 1px solid rgba(155,134,201,0.4); background: rgba(20,8,40,0.55); color: #e9dcff; font-size: 14px; font-family: inherit; }
.ab-prompt { resize: vertical; line-height: 1.55; }

.ab-actions { display: flex; align-items: center; gap: 12px; margin-top: 22px; flex-wrap: wrap; }
.ab-btn { background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #fff; border: none; border-radius: 999px;
  padding: 11px 22px; font-size: 14px; font-weight: 700; cursor: pointer; box-shadow: 0 8px 20px -10px rgba(78,39,118,0.9); }
.ab-btn:disabled { opacity: 0.5; cursor: default; }
.ab-cost { font-size: 12px; color: #9b86c9; }

.ab-gallery { margin-top: 20px; border-top: 1px solid rgba(255,255,255,0.08); padding-top: 14px; }
.ab-gallery-label { font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase; color: rgba(255,255,255,0.35); }
.ab-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(96px, 1fr)); gap: 10px; margin-top: 10px; }
.ab-thumb { position: relative; padding: 0; border: 2px solid transparent; border-radius: 10px; overflow: hidden;
  cursor: pointer; background: none; aspect-ratio: 1; }
.ab-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.ab-thumb.on { border-color: #f0d9a3; }
.ab-thumb-tag { position: absolute; bottom: 4px; right: 4px; font-size: 9px; letter-spacing: 0.06em;
  text-transform: uppercase; color: #1a0a2e; background: #f0d9a3; border-radius: 4px; padding: 1px 5px; font-weight: 700; }

/* Full-image lightbox — review then set live. */
.ab-lightbox { position: fixed; inset: 0; z-index: 1400; background: rgba(0,0,0,0.85);
  display: flex; align-items: center; justify-content: center; padding: 22px; }
.ab-lightbox-card { display: flex; flex-direction: column; align-items: center; gap: 16px; max-width: 92vw; }
.ab-lightbox-img { max-width: 90vw; max-height: 74vh; border-radius: 16px; border: 1px solid rgba(240,217,163,0.25);
  box-shadow: 0 20px 60px -16px rgba(0,0,0,0.9); }
.ab-lightbox-actions { display: flex; gap: 12px; align-items: center; }
.ab-lightbox-ghost { background: transparent; color: #9b86c9; border: 1px solid rgba(155,134,201,0.45);
  border-radius: 999px; padding: 9px 20px; font-size: 14px; cursor: pointer; font-family: inherit; }
.ab-lightbox-set { background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #fff; border: none;
  border-radius: 999px; padding: 9px 24px; font-size: 14px; font-weight: 700; cursor: pointer; font-family: inherit; }
.ab-lightbox-set:disabled { opacity: 0.55; cursor: default; }
.ab-lightbox-live { background: #f0d9a3; color: #1a0a2e; border: none; border-radius: 999px;
  padding: 9px 24px; font-size: 14px; font-weight: 700; font-family: inherit; }
</style>
